import { format } from 'date-fns';
import { ExternalLink, X } from 'lucide-react';
import { stringify } from 'query-string';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router';

import { useAdminLinkInBios } from '@dashboard/hooks/queries/useAdminQueries';
import useSearchInput from '@dashboard/hooks/useSearchInput';
import useUrlQuery from '@dashboard/hooks/useUrlQuery';
import {
  getLinkInBioSharedUrl,
  LINK_IN_BIO_SELLER_VISIT,
  withLinkInBioQuery,
} from '@dashboard/services/getLinkInBioUrl';

import { DataGrid, DataGridColumnHeader } from '@dashboard/components/DataGrid';
import EmptyState from '@dashboard/components/EmptyState';
import { Label, TextInput } from '@dashboard/components/Form';
import Link from '@dashboard/components/Link';
import Tooltip from '@dashboard/components/Tooltip';
import { Badge } from '@dashboard/components/ui/badge';

type LinkInBioRow = {
  _id: string;
  title: string | null;
  blocks: number;
  live: boolean;
  createdAt: string;
  updatedAt: string;
  seller: {
    _id: string;
    name: string;
    displayName: string | null;
    customDomain: string | null;
    customDomainVerified: boolean;
    homepage: { type: string | null };
  };
};

const formatDate = (value: string) => format(new Date(value), 'dd MMM yyyy HH:mm');

const empty = <span className="text-zinc-400 dark:text-muted-foreground">—</span>;

const LinkInBios = () => {
  const history = useHistory();
  const query = useUrlQuery();

  const sortBy = typeof query.sortBy === 'string' ? query.sortBy : 'updatedAt';
  const sortDirection = Number(query.sortDirection) === 1 ? 1 : -1;
  const { search, searchInput, setSearchInput } = useSearchInput(
    query.search == null ? '' : String(query.search),
  );

  const { data, fetchNextPage, hasNextPage, isLoading, isFetchingNextPage } = useAdminLinkInBios({
    sortBy,
    sortDirection,
    search,
    limit: 20,
  });

  const sorting = useMemo(
    () => [{ id: sortBy, desc: sortDirection === -1 }],
    [sortBy, sortDirection],
  );

  const flatData: LinkInBioRow[] = useMemo(
    () => data?.pages?.flatMap((page: any) => page.data) ?? [],
    [data],
  );

  const total: number | undefined = data?.pages[0]?.total;

  const replaceUrl = (
    changes: Partial<{ sortBy: string; sortDirection: number; search: string }>,
  ) => {
    const next = { sortBy, sortDirection, search: searchInput.trim(), ...changes };
    history.replace({
      search: stringify({
        sortBy: next.sortBy,
        sortDirection: next.sortDirection,
        ...(next.search && { search: next.search }),
      }),
    });
  };

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (newSorting.length > 0) {
      const { id, desc } = newSorting[0];
      replaceUrl({ sortBy: id, sortDirection: desc ? -1 : 1 });
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    replaceUrl({ search: value.trim() });
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'seller',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Store" />,
        cell: ({ row }: any) => {
          const { seller } = row.original as LinkInBioRow;
          return (
            <Link to={`/admin/merchants/${seller._id}`}>{seller.displayName || seller.name}</Link>
          );
        },
        enableSorting: false,
      },
      {
        id: 'link',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Link" />,
        cell: ({ row }: any) => {
          const url = getLinkInBioSharedUrl(row.original.seller);
          if (!url) return empty;
          // The seller-visit flag stops the store from counting an admin's view and clicks.
          return (
            <Link
              to={withLinkInBioQuery(url, LINK_IN_BIO_SELLER_VISIT)}
              blank
              className="inline-flex items-center gap-x-1"
            >
              {url}
              <ExternalLink className="size-4 shrink-0" />
            </Link>
          );
        },
        enableSorting: false,
      },
      {
        accessorKey: 'blocks',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Blocks" />,
        cell: ({ row }: any) => row.original.blocks.toLocaleString(),
        enableSorting: true,
      },
      {
        accessorKey: 'live',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Status" />,
        cell: ({ row }: any) =>
          row.original.live ? (
            <Badge variant="success">Live</Badge>
          ) : (
            <Tooltip
              label="Published, but the store is disabled, over its plan limit, canceled or has link in bio switched off"
              side="bottom"
            >
              <span className="cursor-help">
                <Badge variant="warning">Offline</Badge>
              </span>
            </Tooltip>
          ),
        enableSorting: false,
      },
      {
        accessorKey: 'updatedAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Last saved" />,
        cell: ({ row }: any) => formatDate(row.original.updatedAt),
        enableSorting: true,
      },
      {
        accessorKey: 'createdAt',
        header: ({ column }: any) => <DataGridColumnHeader column={column} title="Created" />,
        cell: ({ row }: any) => formatDate(row.original.createdAt),
        enableSorting: true,
      },
    ],
    [],
  );

  const filterControls = (
    <div className="flex items-end gap-x-2">
      <div className="mr-auto flex h-9 items-center text-sm text-gray-500 dark:text-muted-foreground">
        {total !== undefined && `${total} published`}
      </div>

      <div>
        <Label htmlFor="search">Search</Label>
        <div className="w-64">
          <div className="relative flex items-center">
            <TextInput
              border
              id="search"
              name="search"
              onChange={(e: any) => handleSearchChange(e.target.value)}
              placeholder="Search by store or title"
              value={searchInput}
            />
            {searchInput && (
              <button
                aria-label="Clear search"
                onClick={() => handleSearchChange('')}
                className="flex items-center absolute right-0 top-0 bottom-0 p-1.5 m-px bg-white dark:bg-background text-gray-300 dark:text-muted-foreground hover:text-gray-400 dark:hover:text-muted-foreground transition-colors rounded"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pr-4">
      <div className="flex items-end justify-between mb-4">
        <h1>Link in bio</h1>
        {filterControls}
      </div>

      <DataGrid
        columns={columns}
        data={flatData}
        isLoading={isLoading}
        paginationMode="infinite"
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        emptyState={
          <EmptyState>
            <EmptyState.Title>No published link in bio pages found</EmptyState.Title>
          </EmptyState>
        }
      />
    </div>
  );
};

export default LinkInBios;
